import React, { useEffect, useCallback } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CSpinner,
  CFormInput,
  CProgressBar,
  CFormCheck,
  CFormLabel,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload } from "@coreui/icons";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

const CameraModals = ({
  device,
  recalcModalVisible,
  setRecalcModalVisible,
  recalcStartDate,
  setRecalcStartDate,
  // Только у самосвала: с ними окно пересчета спрашивает период (начало и конец)
  recalcEndDate,
  setRecalcEndDate,
  handleRecalculateSubmit,
  recalculating,
  viewerModalVisible,
  setViewerModalVisible,
  viewerUrl,
  isModelLoading,
  setIsModelLoading,
  uploadModalVisible,
  setUploadModalVisible,
  handleFileChange,
  uploadFile,
  isUploading,
  isProcessing,
  uploadProgress,
  uploadReport,
  handleUploadArchive,
  formatModalVisible,
  setFormatModalVisible,
  formatMode,
  setFormatMode,
  formatDate,
  setFormatDate,
  formatConfirmation,
  setFormatConfirmation,
  handleClearData,
  formatting,
  // Новые пропсы для управления видимостью и восстановления
  formatStats,
  formatActionTab,
  setFormatActionTab,
  handleRestoreData,
  exportModalVisible,
  setExportModalVisible,
  exportStart,
  setExportStart,
  exportEnd,
  setExportEnd,
  handleExportVideo,
  isExporting,
}) => {
  const { t } = useTranslation();

  // Функция форматирования текста для кулдауна
  const getCooldownText = (minutes) => {
    if (!minutes) return "1 час"; // Фолбэк на случай отсутствия данных
    
    // Если делится на 60 без остатка, показываем в часах
    if (minutes % 60 === 0) {
      const hours = minutes / 60;
      if (hours === 1) return "1 час";
      if (hours > 1 && hours < 5) return `${hours} часа`;
      return `${hours} часов`;
    }
    
    // Иначе в минутах
    return `${minutes} мин.`;
  };

  const cooldownText = getCooldownText(device?.export_cooldown_min);

  const viewerRefCallback = useCallback(
    (node) => {
      if (node !== null) {
        const handleModelReady = () => setIsModelLoading(false);
        node.addEventListener("load", handleModelReady);
        node.addEventListener("error", handleModelReady);
        node.addEventListener("model-visibility", (e) => {
          if (e.detail.visible) setIsModelLoading(false);
        });
        node.addEventListener("poster-dismissed", handleModelReady);
      }
    },
    [setIsModelLoading],
  );

  useEffect(() => {
    let timeout;
    if (viewerModalVisible && isModelLoading) {
      timeout = setTimeout(() => setIsModelLoading(false), 8000);
    }
    return () => clearTimeout(timeout);
  }, [viewerModalVisible, isModelLoading, setIsModelLoading]);

  return (
    <>
      <CModal
        visible={recalcModalVisible}
        onClose={() => setRecalcModalVisible(false)}
      >
        <CModalHeader onClose={() => setRecalcModalVisible(false)}>
          <CModalTitle>{t("camera.recalc_modal_title")}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {setRecalcEndDate ? (
            <>
              <div className="alert alert-info small mb-3">
                Архивы IMU за период будут заново разобраны воркером в фоне, по порядку времени.
                Пока пересчет идет, на странице остается прежний результат. Обновите аналитику
                после окончания пересчета.
              </div>
              <div className="mb-3">
                <CFormLabel className="fw-semibold">Начало периода:</CFormLabel>
                <DatePicker
                  selected={recalcStartDate}
                  onChange={setRecalcStartDate}
                  className="form-control"
                  wrapperClassName="w-100"
                  showTimeSelect
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale="ru"
                  maxDate={recalcEndDate}
                  disabled={recalculating}
                />
              </div>
              <div className="mb-2">
                <CFormLabel className="fw-semibold">Конец периода:</CFormLabel>
                <DatePicker
                  selected={recalcEndDate}
                  onChange={setRecalcEndDate}
                  className="form-control"
                  wrapperClassName="w-100"
                  showTimeSelect
                  dateFormat="dd.MM.yyyy HH:mm"
                  locale="ru"
                  minDate={recalcStartDate}
                  disabled={recalculating}
                />
              </div>
              {recalcStartDate && recalcEndDate && recalcEndDate <= recalcStartDate && (
                <div className="text-danger small">Конец периода должен быть позже начала</div>
              )}
            </>
          ) : (
            <>
              <p>{t("camera.recalc_modal_body")}</p>
              <DatePicker
                selected={recalcStartDate}
                onChange={setRecalcStartDate}
                className="form-control"
                showTimeSelect
                dateFormat="dd.MM.yyyy HH:mm"
                locale="ru"
              />
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setRecalcModalVisible(false)}
          >
            {t("common.cancel")}
          </CButton>
          <CButton
            color="primary"
            onClick={handleRecalculateSubmit}
            disabled={
              recalculating ||
              (setRecalcEndDate && (!recalcStartDate || !recalcEndDate || recalcEndDate <= recalcStartDate))
            }
          >
            {recalculating ? <CSpinner size="sm" /> : t("camera.start_recalc")}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal
        visible={viewerModalVisible}
        onClose={() => setViewerModalVisible(false)}
        size="lg"
        alignment="center"
      >
        <CModalHeader closeButton className="bg-light">
          <CModalTitle className="fw-bold">Фотоконтроль Цифрового Двойника</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-0 bg-black text-center d-flex align-items-center justify-content-center" style={{ minHeight: "400px" }}>
          {viewerUrl ? (
            <img
              src={viewerUrl}
              alt="Digital Twin Debug"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain"
              }}
            />
          ) : (
            <div className="text-white p-4">Изображение недоступно</div>
          )}
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            onClick={() => setViewerModalVisible(false)}
          >
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal
        visible={uploadModalVisible}
        onClose={() => {
          setUploadModalVisible(false);
          if (isUploading) window.location.reload();
        }}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>{t("camera_modals.upload_title")}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="text-muted small">{t("camera_modals.upload_desc")}</p>
          <CFormInput
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            disabled={isUploading || isProcessing}
            className="mb-3"
          />

          {isUploading && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>{t("camera_modals.uploading_server")}</span>
                <span>{uploadProgress}%</span>
              </div>
              <CProgressBar value={uploadProgress} color="primary" />
            </div>
          )}

          {isProcessing && (
            <div className="text-center mb-3">
              <CSpinner size="sm" variant="grow" className="me-2" />
              <span>{t("camera_modals.unpacking")}</span>
            </div>
          )}

          {uploadReport && (
            <div
              className="mt-3 border p-2 bg-light rounded"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <h6>{t("camera_modals.log")}</h6>
              <ul className="small mb-0 ps-3 list-unstyled">
                {uploadReport.errors &&
                  uploadReport.errors.map((item, idx) => (
                    <li key={`err-${idx}`} className="text-danger mb-1">
                      <strong>{t("camera_modals.error")}</strong> {item}
                    </li>
                  ))}
                {uploadReport.imported &&
                  uploadReport.imported.map((item, idx) => (
                    <li key={`imp-${idx}`} className="text-success mb-1">
                      <strong>{t("camera_modals.ok")}</strong> {item}
                    </li>
                  ))}
                {uploadReport.skipped &&
                  uploadReport.skipped.map((item, idx) => (
                    <li key={`skip-${idx}`} className="text-warning mb-1">
                      <strong>{t("camera_modals.skip")}</strong> {item}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setUploadModalVisible(false)}
            disabled={isUploading}
          >
            {t("camera_modals.close")}
          </CButton>
          <CButton
            color="primary"
            onClick={handleUploadArchive}
            disabled={!uploadFile || isUploading || isProcessing}
          >
            {isUploading || isProcessing
              ? t("camera_modals.wait")
              : t("camera_modals.upload_btn")}
          </CButton>
        </CModalFooter>
      </CModal>

{/* МОДАЛЬНОЕ ОКНО ФОРМАТИРОВАНИЯ И ВОССТАНОВЛЕНИЯ ВИДИМОСТИ */}
      <CModal
        visible={formatModalVisible}
        onClose={() => setFormatModalVisible(false)}
        alignment="center"
      >
        <CModalHeader className="bg-light">
          <CModalTitle className="fw-bold">
            {localStorage.getItem("is_superuser") === "true"
              ? "Управление видимостью (Форматирование)"
              : t("camera_modals.format_title", "Очистка данных")}
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="p-4">
          {/* Блок только для Супер-админа: статистика и вкладка возврата видимости */}
          {localStorage.getItem("is_superuser") === "true" && (
            <>
              <div className="d-flex justify-content-between align-items-center bg-light p-3 rounded border mb-3">
                <div>
                  <span className="text-muted small fw-bold text-uppercase d-block">Техника:</span>
                  <strong className="text-dark">{device?.name}</strong>
                </div>
                {formatStats && (
                  <div className="text-end">
                    <span className="badge bg-success p-2 me-1">
                      Видимых: <b>{formatStats.visible_count}</b>
                    </span>
                    <span className="badge bg-warning text-dark p-2">
                      Скрытых: <b>{formatStats.hidden_count}</b>
                    </span>
                  </div>
                )}
              </div>

              <div className="d-flex border-bottom mb-3">
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none fw-bold pb-2 px-3 border-bottom border-3 rounded-0 ${
                    formatActionTab !== "restore" ? "border-danger text-danger" : "border-transparent text-muted"
                  }`}
                  onClick={() => setFormatActionTab && setFormatActionTab("hide")}
                >
                  🙈 Скрыть данные
                </button>
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none fw-bold pb-2 px-3 border-bottom border-3 rounded-0 ${
                    formatActionTab === "restore" ? "border-success text-success" : "border-transparent text-muted"
                  }`}
                  onClick={() => setFormatActionTab && setFormatActionTab("restore")}
                >
                  👁️ Вернуть видимость {formatStats?.hidden_count > 0 ? `(${formatStats.hidden_count})` : ""}
                </button>
              </div>
            </>
          )}

          {formatActionTab === "restore" && localStorage.getItem("is_superuser") === "true" ? (
            <div>
              <div className="alert alert-info small mb-3">
                Выбранные архивы (<code>show=True</code>) снова появятся у обычных пользователей в календаре, видеоархиве и аналитике.
              </div>
              <div className="mb-3">
                <CFormCheck
                  type="radio"
                  name="formatModeRestore"
                  id="modeAllRestore"
                  label="Вернуть видимость всем скрытым данным"
                  checked={formatMode === "all"}
                  onChange={() => setFormatMode("all")}
                  className="mb-2 fw-semibold"
                />
                <CFormCheck
                  type="radio"
                  name="formatModeRestore"
                  id="modeDateRestore"
                  label="Вернуть видимость данным до выбранной даты"
                  checked={formatMode === "date"}
                  onChange={() => setFormatMode("date")}
                  className="fw-semibold"
                />
              </div>
              {formatMode === "date" && (
                <div className="mb-3 ps-4">
                  <CFormLabel className="fw-semibold small text-muted">Выберите дату:</CFormLabel>
                  <DatePicker
                    selected={formatDate}
                    onChange={setFormatDate}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                    locale="ru"
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-3">
                <CFormCheck
                  type="radio"
                  name="formatModeHide"
                  id="modeAllHide"
                  label={t("camera_modals.format_all", "Удалить все данные")}
                  checked={formatMode === "all"}
                  onChange={() => setFormatMode("all")}
                  className="mb-2 fw-semibold"
                />
                <CFormCheck
                  type="radio"
                  name="formatModeHide"
                  id="modeDateHide"
                  label={t("camera_modals.format_date", "Удалить данные старше даты")}
                  checked={formatMode === "date"}
                  onChange={() => setFormatMode("date")}
                  className="fw-semibold"
                />
              </div>

              {formatMode === "date" && (
                <div className="mb-3 ps-4">
                  <CFormLabel className="fw-semibold small text-muted">
                    {t("camera_modals.select_date", "Выберите дату:")}
                  </CFormLabel>
                  <DatePicker
                    selected={formatDate}
                    onChange={setFormatDate}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                    locale="ru"
                  />
                </div>
              )}

              {localStorage.getItem("is_superuser") !== "true" && (
                <>
                  <div className="alert alert-danger small">
                    {t("camera_modals.format_warning", "Внимание! Действие необратимо.")}
                  </div>
                  {setFormatConfirmation && (
                    <div className="mb-2">
                      <CFormLabel className="small text-muted">
                        {t("camera_modals.enter_device_name", "Введите имя устройства:")}{" "}
                        <strong>{device?.name}</strong>
                      </CFormLabel>
                      <CFormInput
                        value={formatConfirmation || ""}
                        onChange={(e) => setFormatConfirmation(e.target.value)}
                        placeholder={device?.name}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CModalBody>

        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setFormatModalVisible(false)}
            disabled={formatting}
          >
            {t("common.cancel", "Отмена")}
          </CButton>

          {formatActionTab === "restore" && localStorage.getItem("is_superuser") === "true" ? (
            <CButton
              color="success"
              className="text-white fw-bold px-4"
              onClick={handleRestoreData}
              disabled={formatting}
            >
              {formatting ? <CSpinner size="sm" className="me-2" /> : null}
              Вернуть видимость
            </CButton>
          ) : (
            <CButton
              color="danger"
              className="text-white fw-bold px-4"
              onClick={handleClearData}
              disabled={
                formatting ||
                (localStorage.getItem("is_superuser") !== "true" &&
                  formatConfirmation !== undefined &&
                  formatConfirmation !== device?.name)
              }
            >
              {formatting ? <CSpinner size="sm" className="me-2" /> : null}
              {t("camera_modals.format_btn", "Форматировать")}
            </CButton>
          )}
        </CModalFooter>
      </CModal>

      <CModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        alignment="center"
      >
        <CModalHeader className="bg-light">
          <CModalTitle>Склейка и выгрузка видео архива</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="alert alert-info small mb-4">
            <strong>Ограничения:</strong>
            <br />
            1. Максимальный диапазон для одной склейки — 5 суток (120 часов).
            <br />
            2. Выполнять операцию можно не чаще <strong>1 раза в {cooldownText}</strong>.
          </div>
          <div className="mb-3">
            <CFormLabel className="fw-semibold">
              Дата и время начала:
            </CFormLabel>
            <DatePicker
              selected={exportStart}
              onChange={(date) => setExportStart(date)}
              showTimeSelect
              dateFormat="dd.MM.yyyy HH:mm"
              className="form-control"
              locale="ru"
              disabled={isExporting}
            />
          </div>
          <div className="mb-3">
            <CFormLabel className="fw-semibold">
              Дата и время окончания:
            </CFormLabel>
            <DatePicker
              selected={exportEnd}
              onChange={(date) => setExportEnd(date)}
              showTimeSelect
              dateFormat="dd.MM.yyyy HH:mm"
              className="form-control"
              locale="ru"
              disabled={isExporting}
            />
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setExportModalVisible(false)}
            disabled={isExporting}
          >
            {t("common.cancel")}
          </CButton>
          <CButton
            color="primary"
            onClick={handleExportVideo}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <CSpinner size="sm" className="me-2" /> Склейка видео...
              </>
            ) : (
              "Скачать видео"
            )}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default CameraModals;